import { closeX } from './popup.js';
import { $, el } from '../state/store.js';
import { sendFeedback, snapshot } from '../services/feedback.js';
import { currentUser, isAnonymous } from '../services/auth.js';
import { fillContext } from './context-view.js';
import { toast } from './toast.js';

/* FEEDBACK — the same .authwrap/.authcard shell as Settings/Templates/
   Projects, deliberately: this is a modal you open, say one thing in, and
   close, and it should not look like a new part of the app.

   Two fields, one of them optional, and no category dropdown. Asking people
   to classify their own bug report before writing it is the most reliable
   way to get fewer bug reports — sorting them is my job, not theirs. The
   build, view and calibration ride along automatically (services/feedback.js
   attaches them), so the form asks only for what a person actually knows. */

let overlay = null;
function ensureOverlay() {
  if (overlay) return overlay;
  overlay = el('div', 'authwrap hidden');
  overlay.id = 'feedbackwrap';
  document.body.appendChild(overlay);
  return overlay;
}
const hide = () => ensureOverlay().classList.add('hidden');
const show = () => ensureOverlay().classList.remove('hidden');

export function showFeedback() {
  const w = ensureOverlay();
  w.innerHTML = '';
  const card = el('div', 'authcard');
  /* Not "Report a bug". The button, the tooltip and the note below all say
     this takes anything — a heading that names only faults tells someone with
     a question or a compliment they are in the wrong place, which is how a
     feedback form ends up receiving only complaints. */
  card.appendChild(el('h2', null, 'Send feedback'));
  closeX(card, hide);
  card.appendChild(el('p', 'authnote',
    'Something broken, something missing, or something that nearly worked — all useful. No need to describe your setup: the usual questions are answered below automatically.'));

  const msg = el('textarea', 'fb-msg');
  msg.rows = 6;
  msg.maxLength = 4000;
  msg.placeholder = 'What happened, and what you expected instead.';
  card.appendChild(msg);

  /* Pre-filled when there is an address to pre-fill with. An anonymous
     session has none, and that stays fine — a reply address is the price of
     an answer, not the price of being heard. */
  const user = currentUser();
  const known = (!isAnonymous() && user?.email) || '';
  const mail = el('input', 'fb-mail');
  mail.type = 'email';
  mail.value = known;
  mail.placeholder = 'Email, if you want a reply (optional)';
  card.appendChild(mail);

  /* What is attached, shown rather than described. Collapsed, because almost
     nobody wants to read thirty rows of diagnostics — but open-able, because
     this includes their GPU, timezone and user agent, and "we collect some
     technical information" is not an honest way to say that. Filled in
     asynchronously (the OS/browser versions come from an async Client Hints
     call) so opening the form is never held up by it. */
  const det = el('details', 'fb-what');
  det.appendChild(el('summary', null, 'What gets sent with this'));
  const body = el('div', 'fb-what-body', 'Collecting…');
  det.appendChild(body);
  card.appendChild(det);

  snapshot().then(ctx => {
    fillContext(body, ctx, [['env', 'Your machine'], ['app', 'This project']]);
  }).catch(() => { body.textContent = 'Could not read your setup — the message will still send.'; });

  const err = el('p', 'note note--warn hidden');
  card.appendChild(err);

  const row = el('div', 'authrow');
  const cancel = el('button', 'btn btn--gh', 'Cancel');
  cancel.onclick = hide;
  const send = el('button', 'btn', 'Send');
  send.onclick = async () => {
    err.classList.add('hidden');
    /* Disabled for the round trip, and labelled — a form that looks idle
       while it is working gets sent twice. */
    send.disabled = true;
    send.textContent = 'Sending…';
    try {
      await sendFeedback(msg.value, mail.value);
      hide();
      toast('Thanks — that went through.');
    } catch (e) {
      /* Back into the card, not a toast: the message is still in the box and
         still worth another try, so the failure belongs next to it. */
      err.textContent = e.message;
      err.classList.remove('hidden');
      send.disabled = false;
      send.textContent = 'Send';
    }
  };
  row.appendChild(cancel);
  row.appendChild(send);
  card.appendChild(row);

  w.appendChild(card);
  show();
  msg.focus();
}

export function initFeedback() {
  const btn = $('#btnFeedback');
  if (btn) btn.onclick = showFeedback;
}
